import { api } from "../api";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { TrolleyScene } from "../components/TrolleyScene";
import { closeApp, haptic } from "../telegram";
import { useGame } from "../useGame";

interface View {
  ctx: string;
  a: string;
  b: string;
  source: "mc" | "classic" | "own";
  lang: "uk" | "en";
  pick?: "a" | "b";
  answered: boolean;
}

const COPY = { bad_pick: "Тільки А або Б" };

export function Vahon({ date }: { date?: string | undefined }) {
  const g = useGame<View>("vahon", COPY, date);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const v = s.view;
  const pick = v.pick ?? null;
  const answered = v.answered;

  // Everyone's choice comes back as score: 0 = A, 1 = B.
  const votes = s.group.filter((x) => x.status === "won" && x.score !== null);
  const aCount = votes.filter((x) => x.score === 0).length;
  const bCount = votes.filter((x) => x.score === 1).length;
  const total = aCount + bCount;

  const choose = (p: "a" | "b") => {
    haptic.tap();
    void g.run(() => api.guess<View>("vahon", p, date));
  };

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="ВАГОНЕТКА" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-3">
        <div className="text-tghint">
          <TrolleyScene pick={pick} />
        </div>

        <p className="mt-2 text-center text-[15px] leading-snug" lang={v.lang}>
          {v.ctx}
        </p>

        {!answered ? (
          <div className="mt-5 space-y-2">
            <ChoiceButton label="А" text={v.a} lang={v.lang} onClick={() => choose("a")} disabled={g.busy} />
            <ChoiceButton label="Б" text={v.b} lang={v.lang} onClick={() => choose("b")} disabled={g.busy} />
            <div className="pt-1 text-center text-xs text-tghint">правильної відповіді немає — просто обери</div>
          </div>
        ) : (
          <div className="mt-5 space-y-2">
            <Result label="А" text={v.a} lang={v.lang} mine={pick === "a"} count={aCount} total={total} />
            <Result label="Б" text={v.b} lang={v.lang} mine={pick === "b"} count={bCount} total={total} />
            {total > 0 && (
              <div className="pt-1 text-center text-xs text-tghint">
                {total === 1 ? "поки що тільки ти" : `${total} у чаті вже обрали`}
              </div>
            )}
            {votes.length > 0 && (
              <ul className="mt-3 divide-y divide-[rgba(128,128,128,0.2)] text-sm">
                {votes.map((x) => (
                  <li key={x.userId} className="flex items-center justify-between py-1.5">
                    <span className="font-medium">{x.name}</span>
                    <span className="text-tghint">{x.score === 0 ? "А" : "Б"}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="pt-3 text-center text-[11px] text-tghint">
              {v.source === "mc" ? "дилема з набору MoralChoice (CC BY 4.0)" : v.source === "classic" ? "класична задача про вагонетку" : "власна дилема"}
            </div>
            <button
              type="button"
              onClick={closeApp}
              className="mt-2 w-full rounded-xl bg-tgbutton py-3 text-sm font-semibold text-tgbuttontext active:opacity-80"
            >
              назад у чат
            </button>
          </div>
        )}
      </div>

      <Toast message={g.toast} />
    </Shell>
  );
}

function ChoiceButton({ label, text, lang, onClick, disabled }: { label: string; text: string; lang: string; onClick: () => void; disabled: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-3 rounded-2xl bg-tgsection px-4 py-3.5 text-left active:opacity-70 disabled:opacity-40"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[rgba(128,128,128,0.25)] text-xs font-bold">
        {label}
      </span>
      <span className="text-[15px] leading-snug" lang={lang}>
        {text}
      </span>
    </button>
  );
}

function Result({ label, text, lang, mine, count, total }: { label: string; text: string; lang: string; mine: boolean; count: number; total: number }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className={`relative overflow-hidden rounded-2xl px-4 py-3.5 ${mine ? "bg-tgbutton/20" : "bg-tgsection"}`}>
      <div
        className="absolute inset-y-0 left-0 bg-[rgba(128,128,128,0.18)]"
        style={{ width: `${pct}%`, transition: "width .4s" }}
      />
      <div className="relative flex items-center gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[rgba(128,128,128,0.25)] text-xs font-bold">
          {label}
        </span>
        <span className="flex-1 text-[15px] leading-snug" lang={lang}>
          {text}
        </span>
        <span className="shrink-0 text-sm font-semibold text-tghint">
          {mine ? "твій · " : ""}
          {pct}%
        </span>
      </div>
    </div>
  );
}
